import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-gated';
import { invokeRpc } from './integration-access';

/*
 * <vitesse-endpoint-builder> — the request-builder / response pane of the ENDPOINT
 * viewport (the right side of the op-list + builder split). Postman/Insomnia-shaped:
 * a selected named operation → method badge + url + Invoke, then a params form (left)
 * and a response viewer (right) where each JSON field is a first-class ADDRESSABLE,
 * live-subscribable leaf (the flatten wedge). Self-contained + prop-driven so it renders
 * from a mock in the harness AND from the real schema/value plane in the app; it emits
 * intent events (`param-input`, `invoke`) and owns no data of its own.
 *
 * The one button is GATED on the procedure it stands for — Integrations.Read for
 * a read op (its response rides the value stream), Integrations.Write for a write
 * (integration-access.ts picks). A caller without the grant sees a lock with the
 * reason instead of a button that fails on click. Gating fails open with no
 * session, so the harness mock still renders a live button.
 */

export interface EndpointOp {
  name: string;
  method: string; // GET | POST | PUT | PATCH | DELETE | …
  url: string;
  write?: boolean; // a side-effecting op — Invoke disabled in the browser
}
export interface EndpointParam {
  name: string;
  in: string; // path | query | header | body
  type: string; // string | int | …
  required?: boolean;
  placeholder?: string;
  value?: string;
}
export type FieldKind = 'string' | 'number' | 'bool' | 'null';
export interface EndpointField {
  key: string;
  kind: FieldKind;
  value: string; // display form (already rendered)
  live?: boolean; // an observed live leaf
}
export interface EndpointResponse {
  status: string; // "200 OK"
  ok: boolean;
  timing?: string; // "42 ms · polled 1s"
  fields: EndpointField[];
}

// method → [fg token, low-alpha bg]. GET green (safe/read), mutating verbs warmer/red.
const METHOD_COLORS: Record<string, [string, string]> = {
  GET: ['var(--green)', 'color-mix(in srgb, var(--green) 14%, transparent)'],
  POST: ['var(--amber)', 'color-mix(in srgb, var(--amber) 14%, transparent)'],
  PUT: ['var(--blue)', 'color-mix(in srgb, var(--blue) 14%, transparent)'],
  PATCH: ['var(--purple)', 'color-mix(in srgb, var(--purple) 14%, transparent)'],
  DELETE: ['var(--red)', 'color-mix(in srgb, var(--red) 14%, transparent)'],
};
const methodColor = (m: string): [string, string] => METHOD_COLORS[m.toUpperCase()] ?? ['var(--text-secondary)', 'var(--hover-bg)'];

// JSON value color by scalar kind (keys are sky, matching the design).
const VALUE_COLOR: Record<FieldKind, string> = {
  string: 'var(--green)',
  number: 'var(--blue-light)',
  bool: 'var(--amber)',
  null: 'var(--text-tertiary)',
};

/* The response arrives FLAT — the flatten wedge addresses every leaf by path
 * (`readings/zone0/field0`), which is what makes fields individually live and
 * SCXML-addressable. A flat dump of 185 slash-paths is unreadable, so the viewer
 * re-nests them into the JSON shape they came from and folds the containers. */
interface RNode {
  key: string;
  path: string;
  field?: EndpointField;
  children: RNode[];
}

/* The write-body editor is a plain <textarea> (Monaco is far too heavy for a ten-line
 * request body), so the JSON-editor manners you expect — indent after an opening
 * bracket, dedent when you close it, auto-paired brackets — are implemented on keydown
 * below. INDENT is what JSON.stringify(…, 2) produces, so hand-typed and Format-ed
 * bodies agree. */
const INDENT = '  ';
const PAIRS: Record<string, string> = { '{': '}', '[': ']', '"': '"' };
const CLOSERS = new Set(['}', ']']);
const leadingWs = (s: string): string => /^[ \t]*/.exec(s)![0];

function buildTree(fields: EndpointField[]): RNode {
  const root: RNode = { key: '', path: '', children: [] };
  const byPath = new Map<string, RNode>([['', root]]);
  for (const f of fields) {
    const parts = f.key.split('/');
    let cur = root;
    let acc = '';
    for (let i = 0; i < parts.length; i++) {
      acc = acc ? `${acc}/${parts[i]}` : parts[i];
      let node = byPath.get(acc);
      if (!node) {
        node = { key: parts[i], path: acc, children: [] };
        byPath.set(acc, node);
        cur.children.push(node);
      }
      if (i === parts.length - 1) node.field = f;
      cur = node;
    }
  }
  return root;
}

function leafCount(n: RNode): number {
  if (n.children.length === 0) return 1;
  let c = 0;
  for (const k of n.children) c += leafCount(k);
  return c;
}

@customElement('vitesse-endpoint-builder')
export class EndpointBuilder extends LitElement {
  @property({ attribute: false }) op: EndpointOp | null = null;
  @property({ attribute: false }) params: EndpointParam[] = [];
  @property({ attribute: false }) response: EndpointResponse | null = null;
  @property({ type: Boolean }) invoking = false;
  /** Expanded container paths in the response tree. Containers start folded so a
   *  60-zone payload reads as a handful of lines, not 185. */
  @state() private expanded = new Set<string>();
  /** Request body for a write op (sent as the driver's {"body": …}). */
  @state() private body = '';

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      overflow: hidden;
      background: var(--bg-primary);
    }
    .empty {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-secondary);
      font-size: 13px;
      padding: 28px;
    }
    .bar {
      flex: none;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 11px 16px;
      border-bottom: 1px solid var(--border-color);
      background: var(--tree-bg);
    }
    .method {
      font: 700 11px/1 var(--font);
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
      padding: 3px 8px;
      border-radius: 5px;
      flex: none;
      letter-spacing: 0.03em;
    }
    .url {
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
      font-size: 13.5px;
      color: var(--text-primary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .invoke {
      margin-left: auto;
      flex: none;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 16px;
      border: none;
      border-radius: 6px;
      background: linear-gradient(135deg, var(--blue-light), var(--blue));
      color: var(--on-accent);
      font: 600 13px/1 var(--font);
      cursor: pointer;
      transition: filter 0.15s ease, opacity 0.15s ease;
    }
    .invoke:hover {
      filter: brightness(1.08);
    }
    .invoke:disabled {
      opacity: 0.45;
      cursor: not-allowed;
      filter: none;
    }
    .invoke.write {
      background: none;
      border: 1px solid var(--border-color);
      color: var(--text-tertiary);
    }
    .split {
      flex: 1;
      min-height: 0;
      display: flex;
    }
    .params {
      width: 46%;
      min-width: 0;
      border-right: 1px solid var(--border-color);
      display: flex;
      flex-direction: column;
    }
    .response {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }
    .section-label {
      flex: none;
      padding: 8px 14px;
      font: 600 11px/1.2 var(--font);
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--text-tertiary);
      border-bottom: 1px solid var(--border-color-light);
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .params-body {
      flex: 1;
      overflow-y: auto;
      padding: 12px 14px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .p-head {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 4px;
    }
    .p-name {
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
      font-size: 12.5px;
      color: var(--text-primary);
    }
    .p-in {
      font-size: 10px;
      color: var(--text-tertiary);
      border: 1px solid var(--border-color);
      border-radius: 3px;
      padding: 0 4px;
    }
    .p-req {
      font-size: 14px;
      color: var(--red);
      line-height: 1;
    }
    .p-type {
      margin-left: auto;
      font-size: 10.5px;
      color: var(--text-tertiary);
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    }
    .p-input {
      width: 100%;
      box-sizing: border-box;
      padding: 7px 10px;
      font-size: 12.5px;
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
      background: var(--bg-secondary);
      border: 1px solid var(--border-color);
      border-radius: 6px;
      color: var(--text-primary);
      outline: none;
      transition: border-color 0.15s ease;
    }
    .p-input:focus {
      border-color: var(--blue);
    }
    .p-input.body {
      resize: vertical;
      line-height: 1.5;
      min-height: 140px;
      tab-size: 2;
      white-space: pre;
      overflow-wrap: normal;
      overflow-x: auto;
    }
    .p-format {
      margin-left: auto;
      padding: 1px 8px;
      border: 1px solid var(--border-color);
      border-radius: 4px;
      background: none;
      color: var(--text-tertiary);
      font: 500 10.5px/1.5 var(--font);
      cursor: pointer;
      transition: color 0.15s ease, border-color 0.15s ease;
    }
    .p-format:hover {
      color: var(--text-primary);
      border-color: var(--text-tertiary);
    }
    .no-params {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 36px 12px;
      text-align: center;
      color: var(--text-secondary);
    }
    .no-params svg {
      opacity: 0.5;
    }
    .no-params .hint {
      font-size: 12px;
      color: var(--text-tertiary);
      max-width: 230px;
      line-height: 1.5;
    }
    .resp-head {
      flex: none;
      padding: 8px 14px;
      display: flex;
      align-items: center;
      gap: 10px;
      border-bottom: 1px solid var(--border-color-light);
      font-size: 11px;
    }
    .resp-head .label {
      font-weight: 600;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--text-tertiary);
    }
    .resp-status {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    }
    .resp-status .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }
    .resp-timing {
      color: var(--text-tertiary);
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    }
    .resp-addr {
      margin-left: auto;
      color: var(--ai);
    }
    .resp-body {
      flex: 1;
      overflow: auto;
      padding: 12px 14px;
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
      font-size: 12.5px;
      line-height: 1.7;
    }
    .brace {
      color: var(--text-tertiary);
    }
    .f-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 18px;
      border-radius: 5px;
      transition: background 0.3s ease;
    }
    .f-key {
      color: var(--cm-attr);
    }
    .f-row.obj {
      cursor: pointer;
      user-select: none;
    }
    .f-row.obj:hover {
      background: var(--hover-bg);
    }
    .tw {
      color: var(--text-tertiary);
      width: 10px;
      flex: none;
    }
    .count {
      color: var(--text-tertiary);
      font-size: 11px;
    }
    .f-live {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 10px;
      color: var(--ai);
      border: 1px solid color-mix(in srgb, var(--ai) 35%, transparent);
      border-radius: 10px;
      padding: 1px 8px;
    }
    .f-live .dot {
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--ai);
      animation: vi-pulse 1.5s ease-in-out infinite;
    }
    .resp-empty {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-tertiary);
      font-size: 12.5px;
    }
    @keyframes vi-pulse {
      0%,
      100% {
        opacity: 0.35;
      }
      50% {
        opacity: 1;
      }
    }
  `;

  private onParam(name: string, e: Event): void {
    const value = (e.target as HTMLInputElement).value;
    this.dispatchEvent(new CustomEvent('param-input', { detail: { name, value }, bubbles: true, composed: true }));
  }
  private onInvoke(): void {
    if (this.invoking) return;
    // The button that fires this is wrapped in <vitesse-gated> (render below), so
    // a caller the session knows will be refused never gets here. That is UX,
    // not the check: the api-gateway decides again, and a refusal that still
    // arrives (a stale session, a race with a revocation) comes back as a
    // PermissionDenied with a user-facing message that the viewport shows verbatim.
    this.dispatchEvent(new CustomEvent('invoke', { detail: { body: this.body }, bubbles: true, composed: true }));
  }

  private onBody(e: Event): void {
    this.body = (e.target as HTMLTextAreaElement).value;
  }

  /** Splice `text` over [start,end) and leave the caret at `caret`. Goes through
   *  execCommand where it exists so the edit joins the textarea's native undo stack
   *  (ctrl-Z still walks back through auto-indents); the caret is re-set after Lit
   *  commits the new .value, which would otherwise drop it at the end. */
  private editBody(ta: HTMLTextAreaElement, start: number, end: number, text: string, caret: number): void {
    ta.focus();
    ta.setSelectionRange(start, end);
    let ok = false;
    try {
      ok = document.execCommand('insertText', false, text);
    } catch {
      ok = false;
    }
    if (!ok) ta.setRangeText(text, start, end, 'end');
    ta.setSelectionRange(caret, caret);
    this.body = ta.value;
    void this.updateComplete.then(() => ta.setSelectionRange(caret, caret));
  }

  private onBodyKeydown(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const ta = e.target as HTMLTextAreaElement;
    const v = ta.value;
    const s = ta.selectionStart ?? 0;
    const t = ta.selectionEnd ?? s;
    const lineStart = v.lastIndexOf('\n', s - 1) + 1;
    const indent = leadingWs(v.slice(lineStart));

    if (e.key === 'Enter' && !e.shiftKey) {
      // Open the block one level deeper; if its closer is sitting right after the
      // caret, push it onto its own line so `{|}` becomes the three-line block.
      const before = v.slice(0, s).replace(/[ \t]+$/, '');
      const opener = before[before.length - 1] ?? '';
      const opened = opener === '{' || opener === '[';
      const inner = opened ? indent + INDENT : indent;
      const closerNext = opened && v.slice(t).replace(/^[ \t]*/, '')[0] === PAIRS[opener];
      e.preventDefault();
      this.editBody(ta, s, t, closerNext ? `\n${inner}\n${indent}` : `\n${inner}`, s + 1 + inner.length);
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      if (s !== t && v.slice(s, t).includes('\n')) {
        // Block indent/outdent over whole lines.
        const blockEnd = v.indexOf('\n', t) === -1 ? v.length : v.indexOf('\n', t);
        const block = v.slice(lineStart, blockEnd);
        const next = block
          .split('\n')
          .map((l) => (e.shiftKey ? l.replace(new RegExp(`^ {1,${INDENT.length}}|^\t`), '') : l && INDENT + l))
          .join('\n');
        this.editBody(ta, lineStart, blockEnd, next, lineStart + next.length);
        return;
      }
      if (e.shiftKey) {
        const cut = Math.min(INDENT.length, indent.length);
        if (cut) this.editBody(ta, lineStart, lineStart + cut, '', Math.max(lineStart, s - cut));
        return;
      }
      this.editBody(ta, s, t, INDENT, s + INDENT.length);
      return;
    }

    if (e.key === 'Backspace' && s === t && s > lineStart && !v.slice(lineStart, s).trim()) {
      // Inside a run of indentation — eat a whole level, not one space.
      const cut = ((s - lineStart - 1) % INDENT.length) + 1;
      e.preventDefault();
      this.editBody(ta, s - cut, s, '', s - cut);
      return;
    }

    if (CLOSERS.has(e.key) && s === t) {
      const lead = v.slice(lineStart, s);
      const overtype = v[s] === e.key; // the auto-inserted partner is already there
      if (lead.length > 0 && !lead.trim()) {
        e.preventDefault();
        const ded = lead.slice(0, Math.max(0, lead.length - INDENT.length));
        this.editBody(ta, lineStart, overtype ? s + 1 : s, ded + e.key, lineStart + ded.length + 1);
      } else if (overtype) {
        e.preventDefault();
        ta.setSelectionRange(s + 1, s + 1);
      }
      return;
    }

    if (e.key === '"' && s === t && v[s] === '"') {
      e.preventDefault();
      ta.setSelectionRange(s + 1, s + 1);
      return;
    }

    const close = PAIRS[e.key];
    if (close && s === t && !/[\w"]/.test(v[s] ?? '')) {
      e.preventDefault();
      this.editBody(ta, s, t, e.key + close, s + 1);
    }
  }

  /** Pretty-print the body — for pasted-in one-liners, where typing manners can't help. */
  private formatBody(): void {
    try {
      const pretty = JSON.stringify(JSON.parse(this.body), null, INDENT.length);
      if (pretty !== this.body) {
        const ta = this.renderRoot.querySelector<HTMLTextAreaElement>('#wbody');
        if (ta) this.editBody(ta, 0, ta.value.length, pretty, pretty.length);
        else this.body = pretty;
      }
    } catch {
      /* not valid JSON yet — leave what they typed alone */
    }
  }

  protected render(): TemplateResult {
    const op = this.op;
    if (!op) return html`<div class="empty">Select an operation to inspect it.</div>`;
    const [fg, bg] = methodColor(op.method);
    return html`
      <div class="bar">
        <span class="method" style="color:${fg}; background:${bg};">${op.method.toUpperCase()}</span>
        <span class="url">${op.url}</span>
        <vitesse-gated .rpc=${invokeRpc(op)} inline>
          <button
            class="invoke"
            ?disabled=${this.invoking}
            title=${op.write ? 'Send this write' : 'Invoke this read'}
            @click=${this.onInvoke}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
            ${this.invoking ? (op.write ? 'Sending…' : 'Invoking…') : op.write ? 'Send' : 'Invoke'}
          </button>
        </vitesse-gated>
      </div>
      <div class="split">
        <div class="params">
          <div class="section-label">${op.write ? 'Body / params' : 'Parameters'}</div>
          <div class="params-body">${this.renderParams()}</div>
        </div>
        <div class="response">${this.renderResponse()}</div>
      </div>
    `;
  }

  private renderParams(): TemplateResult {
    // A write op gets a real body editor — this pane is where you compose the
    // request. Permission to actually send it is the server's call, not this
    // form's; an unauthorized send returns a message telling you to ask an admin.
    if (this.op?.write) {
      return html`
        <label class="p-head" for="wbody">
          <span class="p-name">body</span><span class="p-in">json</span>
          <button class="p-format" type="button" title="Pretty-print this body" @click=${() => this.formatBody()}>
            Format
          </button>
        </label>
        <textarea
          id="wbody"
          class="p-input body"
          rows="10"
          spellcheck="false"
          autocapitalize="off"
          autocorrect="off"
          placeholder=${'{\n  "value": 42\n}'}
          .value=${this.body}
          @input=${(e: Event) => this.onBody(e)}
          @keydown=${(e: KeyboardEvent) => this.onBodyKeydown(e)}
        ></textarea>
      `;
    }
    if (this.params.length === 0) {
      return html`
        <div class="no-params">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
          </svg>
          <div style="font-size:13px;">${this.op?.write ? 'No declared body' : 'No parameters'}</div>
          <div class="hint">
            ${this.op?.write
              ? html`A write op — its body is supplied by the model that calls it. The browser never
                  writes integration data (writes are model → service by design).`
              : html`A no-param read — its response fields are subscribed live and appear in the
                  response, individually addressable.`}
          </div>
        </div>
      `;
    }
    return html`${this.params.map(
      (p) => html`
        <div>
          <div class="p-head">
            <span class="p-name">${p.name}</span>
            <span class="p-in">${p.in}</span>
            ${p.required ? html`<span class="p-req">*</span>` : nothing}
            <span class="p-type">${p.type}</span>
          </div>
          <input
            class="p-input"
            .value=${p.value ?? ''}
            placeholder=${p.placeholder ?? ''}
            @input=${(e: Event) => this.onParam(p.name, e)}
          />
        </div>
      `,
    )}`;
  }

  private renderResponse(): TemplateResult {
    const r = this.response;
    if (!r) {
      return html`
        <div class="resp-head"><span class="label">Response</span></div>
        <div class="resp-empty">${this.op?.write ? 'Invoked via models — no live response here.' : 'Invoke to see the response.'}</div>
      `;
    }
    const sc = r.ok ? 'var(--green)' : 'var(--red)';
    return html`
      <div class="resp-head">
        <span class="label">Response</span>
        <span class="resp-status" style="color:${sc};"><span class="dot" style="background:${sc};"></span>${r.status}</span>
        ${r.timing ? html`<span class="resp-timing">${r.timing}</span>` : nothing}
        <span class="resp-addr">fields are addressable ↓</span>
      </div>
      <div class="resp-body">
        <div class="brace">{</div>
        ${buildTree(r.fields).children.map((n) => this.renderNode(n, 0))}
        <div class="brace">}</div>
      </div>
    `;
  }

  private toggle(path: string): void {
    const next = new Set(this.expanded);
    if (!next.delete(path)) next.add(path);
    this.expanded = next;
  }

  /** One response node: a scalar leaf, or a foldable container showing its leaf
   *  count while collapsed. */
  private renderNode(n: RNode, depth: number): TemplateResult {
    const pad = 18 + depth * 14;
    if (n.children.length === 0) {
      const f = n.field;
      return html`
        <div class="f-row" style="padding-left:${pad}px">
          <span class="f-key">"${n.key}"</span><span class="brace">:</span>
          <span style="color:${f ? VALUE_COLOR[f.kind] : 'inherit'};">${f?.value ?? ''}</span>
          ${f?.live ? html`<span class="f-live"><span class="dot"></span>live leaf</span>` : nothing}
        </div>
      `;
    }
    const open = this.expanded.has(n.path);
    return html`
      <div class="f-row obj" style="padding-left:${pad}px" @click=${() => this.toggle(n.path)}>
        <span class="tw">${open ? '▾' : '▸'}</span>
        <span class="f-key">"${n.key}"</span><span class="brace">:</span>
        <span class="brace">{</span>
        ${open ? nothing : html`<span class="count">${leafCount(n)} fields</span><span class="brace">}</span>`}
      </div>
      ${open
        ? html`${n.children.map((c) => this.renderNode(c, depth + 1))}
            <div class="brace" style="padding-left:${pad}px">}</div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-endpoint-builder': EndpointBuilder;
  }
}
